import { MouseEvent, useContext } from "react";
import { UNSAFE_NavigationContext } from "react-router";
import { LayoutContext } from "@/contexts/LayoutContext";
import { isLegacyShellPath, toSameOriginPath } from "@/utils/legacyShell";
import { popupCenter } from "@/utils/popup";
import { MenuItem } from "@/types/domain";

export const useMenuNavigation = () => {
  const layout = useContext(LayoutContext);
  const navigator = useContext(UNSAFE_NavigationContext)?.navigator;

  const openPopup = (item: MenuItem) => {
    popupCenter(
      item.url ?? "#",
      item.popupWindowName ?? "_blank",
      item.popupWidth ?? 1024,
      item.popupHeight ?? 768,
    );
  }

  const handlePopupClick = (e: MouseEvent, item: MenuItem) => {
    e.preventDefault();
    openPopup(item);
  }

  const tryNavigateInShell = (item: MenuItem): boolean => {
    const legacyShell = layout?.data.legacyShell;
    if (!navigator || !legacyShell?.enabled || !item.url) return false;
    const path = toSameOriginPath(item.url);
    if (!path || !isLegacyShellPath(new URL(path, window.location.origin).pathname, legacyShell)) return false;
    navigator.push(path);
    return true;
  }

  const handleLinkClick = (e: MouseEvent<HTMLAnchorElement>, item: MenuItem) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (e.currentTarget.target && e.currentTarget.target !== "_self") return;
    if (tryNavigateInShell(item)) e.preventDefault();
  }

  const activate = (item: MenuItem) => {
    if (item.type === "popup") {
      openPopup(item);
      return;
    }
    if (!item.url || tryNavigateInShell(item)) return;
    window.location.href = item.url;
  }

  return { handlePopupClick, handleLinkClick, activate };
}
